<template>
    <b-card
        :title="title"
        :loading="loading"
        :error-message="errorMessage"
        :disabled="mode === 1 ? !formValid : false"
        @cancel="$emit('cancel')"
        @submit="mode === 1 ? create() : update(query)"
        :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
        :cancel-text="{ el: 'Ακύρωση', en: 'Cancel', it: 'Cancella' }"
    >
        <v-row no-gutters>
            <v-col cols="6" class="pr-2">
                <b-select
                    v-model="store.store_subscription_plan_id"
                    :items="subscriptionPlans"
                    :item-text="`plan_name[${lang}]`"
                    item-value="store_subscription_plan_id"
                    :label="translations.selectPackage[lang]"
                    no-top-margin
                    :success="success.subscriptionPlan"
                    :error-messages="error.subscriptionPlan"
                    @focus="error.subscriptionPlan = ''"
                    @blur="validateSubscriptionPlan"
                ></b-select>
            </v-col>

            <v-col cols="6" class="pl-2">
                <b-select
                    v-model="store.country_id"
                    :items="countries"
                    item-text="name"
                    item-value="country_id"
                    :label="translations.country[lang]"
                    no-top-margin
                    :success="success.country"
                    :error-messages="error.country"
                    @focus="error.country = ''"
                    @change="validateCountry"
                ></b-select>
            </v-col>

            <v-col cols="6" class="pr-2">
                <b-text-field
                    v-model="store.name"
                    :label="translations.storeNameModal[lang]"
                    :success="success.name"
                    :error-messages="error.name"
                    @focus="error.name = ''"
                    @blur="validateName"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pl-2">
                <b-text-field
                    v-model="store.app_name"
                    :label="translations.storeName[lang]"
                    :success="success.app_name"
                    :error-messages="error.app_name"
                    @focus="error.app_name = ''"
                    @blur="validateAppName"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pr-2">
                <b-text-field
                    v-model="store.store_code"
                    :label="translations.code[lang]"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pl-2">
                <b-text-field
                    v-model="store.vat_number"
                    :label="translations.vatNumber[lang]"
                    :success="success.vatNumber"
                    :error-messages="error.vatNumber"
                    :disabled="!store.country_id"
                    @blur="validateVatNumber"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pr-2">
                <b-text-field
                    v-model="store.email"
                    type="email"
                    :label="translations.emailOfStore[lang]"
                    :success="success.email"
                    :error-messages="error.email"
                    @focus="error.email = ''"
                    @blur="validateEmail"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pl-2">
                <b-text-field
                    v-model="store.primary_phone"
                    v-mask="'############'"
                    :label="translations.primaryPhone[lang]"
                    :success="success.primaryPhone"
                    :error-messages="error.primaryPhone"
                    @focus="error.primaryPhone = ''"
                    @blur="validatePrimaryPhone"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pr-2">
                <b-select
                    v-model="store.store_company_network_region_id"
                    :items="networkRegions"
                    item-text="name"
                    item-value="store_company_network_region_id"
                    :label="translations.networkRegion[lang]"
                ></b-select>
            </v-col>

            <v-col cols="6" class="pl-2" v-if="!store.manually_address">
                <Places
                    :initial-address="store.address"
                    :label="translations.address[lang]"
                    class="mt-3"
                    outlined
                    dense
                    hide-details="auto"
                    ref="places"
                    :success="success.address"
                    :error-messages="error.address"
                    @focus="error.address = ''"
                    @blur="validateAddress"
                    @newAddress="setAddressDetails"
                />
            </v-col>

            <v-col cols="12">
                <v-checkbox
                    v-model="store.manually_address"
                    color="secondary"
                    class="mt-3 pt-0"
                    hide-details="auto"
                >
                    <template v-slot:label>
                        <h4 class="secondary--text">
                            {{ translations.addCoordinates[lang] }}
                        </h4>
                    </template>
                </v-checkbox>
            </v-col>

            <v-col cols="6" class="pr-2" v-if="store.manually_address">
                <b-text-field
                    v-model="store.address"
                    :label="translations.address[lang]"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pl-2" v-if="store.manually_address">
                <b-text-field
                    v-model="store.zip"
                    type="number"
                    :label="translations.zip[lang]"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pr-2" v-if="store.manually_address">
                <b-text-field
                    v-model="store.lat"
                    type="number"
                    :label="translations.latitute[lang]"
                ></b-text-field>
            </v-col>

            <v-col cols="6" class="pl-2" v-if="store.manually_address">
                <b-text-field
                    v-model="store.lon"
                    type="number"
                    :label="translations.longitude[lang]"
                ></b-text-field>
            </v-col>
        </v-row>

        <v-row no-gutters>
            <v-col cols="12" v-if="mode === 1">
                <v-checkbox
                    v-model="userDetails.new_manager"
                    color="secondary"
                    class="mt-3 pt-0"
                    hide-details="auto"
                >
                    <template v-slot:label>
                        <h4 class="secondary--text">
                            {{ translations.addNewStoreManager[lang] }}
                        </h4>
                    </template>
                </v-checkbox>
            </v-col>

            <template v-if="userDetails.new_manager && mode === 1">
                <v-col cols="7" class="pr-2 mb-4">
                    <b-text-field
                        v-model="userDetails.full_name"
                        :label="translations.fullName[lang]"
                        :success="success.fullname"
                        :error-messages="error.fullname"
                        @focus="error.fullname = ''"
                        @blur="validateFullname"
                    ></b-text-field>
                </v-col>
                <v-col cols="7" class="pr-2 d-flex">
                    <v-select
                        :items="countries"
                        label="CC*"
                        outlined
                        v-model="userDetails.country_id"
                        item-value="country_id"
                        no-top-margin
                        item-text="name"
                        solo
                        flat
                        dense
                        hide-details="auto"
                        color="secondary"
                        item-color="secondary"
                        class="country-code-select"
                        :success="success.countryCode"
                        :error-messages="error.countryCode"
                        @focus="error.countryCode = ''"
                        @blur="validateCountryCode"
                    >
                    <template v-slot:selection="{ item }">
                                
                        <v-row no-gutters class="d-flex align-center">
                            <v-col class="mr-0">
                                <img
                                    :src="item.flag"
                                    width="25"
                                    style="vertical-align: middle"
                                    class="mr-0"
                                />
                            </v-col>

                            <v-col cols="auto"
                                >{{ item.phone_code }}</v-col
                            >
                        </v-row>
                    </template>
                    <template v-slot:item="{ item }">
                        <v-row no-gutters>
                            <v-col class="mr-3">
                                <img
                                    :src="item.flag"
                                    width="25"
                                    style="vertical-align: middle"
                                    class="mr-3"
                                />
                                {{ item.name }} ({{ item.code }})
                            </v-col>

                            <v-col cols="auto"
                                >{{ item.phone_code }}</v-col
                            >
                        </v-row>
                    </template>
                    </v-select>
                    <b-text-field
                        v-model="userDetails.mobile"
                        v-mask="'############'"
                        :label="translations.mobile[lang]"
                        no-top-margin
                        :success="success.mobile"
                        :error-messages="error.mobile"
                        @focus="error.mobile = ''"
                        @blur="validateMobile"
                        class="user-mobile"
                    >
                    </b-text-field>
                </v-col>
            </template>
        </v-row>

        <v-row class="pl-3" v-if="userDetails.new_manager && mode === 1">
            <v-checkbox
                v-model="userDetails.tos"
                color="secondary"
                class="mt-3 pt-0"
                hide-details="auto"
                :success="success.tos"
                :error-messages="error.tos"
            ></v-checkbox>

            <span
                class="d-flex align-end pointer"
                @click="userDetails.tos = !userDetails.tos"
            >
                {{ translations.iAccept[lang] }}
                <a
                    class="blue--text mx-1"
                    target="blank"
                    href="https://roadcube-production-files.s3.eu-central-1.amazonaws.com/terms/tos_roadcube_gr.pdf"
                >
                    {{ translations.terms[lang] }}
                </a>
                {{ translations.and[lang] }}

                <a
                    class="blue--text ml-1"
                    target="blank"
                    href="https://roadcube-production-files.s3.eu-central-1.amazonaws.com/terms/privacy_roadcube_gr.pdf"
                >
                    {{ translations.privacyPolicy[lang] }}
                </a>
            </span>
        </v-row>

        <v-row class="pl-3" v-if="userDetails.new_manager && mode === 1">
            <v-checkbox
                v-model="userDetails.marketing"
                color="secondary"
                class="mt-3 pt-0"
                hide-details="auto"
                :success="success.marketing"
                :error-messages="error.marketing"
            ></v-checkbox>

            <span
                class="d-flex align-end pointer"
                @click="userDetails.marketing = !userDetails.marketing"
            >
                {{ translations.iAccept[lang] }}
                <span
                    class="blue--text ml-1"
                    @click="openNewTab(`/${lang}/direct-marketing`)"
                >
                    {{ translations.digitalMarketing[lang] }}
                </span>
            </span>
        </v-row>
    </b-card>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";

import Places from "@/components/general/Places.vue";
import validators from "@/utils/validators/loyaltyPanel/store";
import translations from "@/utils/translations/loyaltyPanel/stores/storesTab";

export default {
    name: "StoreForm",

    components: { Places },

    mixins: [validators, translations],

    props: {
        mode: Number,
        query: String,
    },

    data: () => ({
        isAddress: false,
    }),

    computed: {
        ...mapState([
            "loading",
            "errorMessage",
            "resetSuccess",
            "resetValidation",
        ]),
        ...mapState("loyaltyPanel/stores/storesTab", [
            "subscriptionPlans",
            "countries",
            "networkRegions",
            "store",
            "userDetails",
            "newStoreCreated",
        ]),

        lang() {
            return this.$route.params.lang;
        },

        title() {
            return this.mode === 1
                ? this.translations.newStore[this.lang]
                : this.translations.updateStore[this.lang];
        },
    },

    methods: {
        ...mapMutations(["setResetSuccess", "setResetValidation"]),
        ...mapActions(["checkVatNumber"]),
        ...mapActions("loyaltyPanel/stores/storesTab", [
            "getSubscriptionPlans",
            "getCountries",
            "getNetworkRegions",
            "getItem",
            "create",
            "update",
        ]),

        setAddressDetails(place) {
            this.isAddress = place.isAddress;
            this.store.address = place.address;
            this.store.zip = place.zip;
            this.store.lat = place.lat;
            this.store.lon = place.lon;
        },

        openNewTab(url) {
            let routeData = this.$router.resolve({ path: url });
            window.open(routeData.href, "_blank");
        },
    },

    watch: {
        newStoreCreated(val) {
            if (val) {
                this.$refs.places.clearInput();
            }
        },

        store(val) {
            this.$refs.places.clearInput();
        },

        ["userDetails.new_manager"](val) {
            if (val && !this.userDetails.country_id) {
                this.userDetails.country_id = this.store.country_id || null;
            }
        },
    },

    mounted() {
        this.getSubscriptionPlans();
        this.getCountries();
        this.getNetworkRegions();
        if (this.mode === 2) this.isAddress = true;
    },
};
</script>
<style lang="scss">
.country-code-select .v-input__slot {
    min-width: 110px;
    padding: 0 4px 0 8px !important;
}
.country-code-select .v-select__selections input {
    padding: 0 !important;
    margin: 0 !important;
    height: 0 !important;
    width: 0 !important;
    overflow: hidden !important;
}

.country-code-select .v-input__append-inner {
    padding-left: 0 !important;
}

.country-code-select .v-input__append-inner .v-icon.v-icon::after {
    max-width: 20px;
    min-width: 20px;
}

.country-code-select fieldset {
    border-right: 1px dotted black !important;
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
}
.user-mobile fieldset {
    border-left: none !important;
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
}
</style>
